import { cva } from 'class-variance-authority';

/** Geometry from Figma set 2034:133442. Colour and state live on .ds-card. */
export const cardRecipe = cva(
  [
    'ds-card relative isolate box-border flex items-start gap-3 rounded-md border p-4 font-sans',
    'transition-[background-color,border-color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
  ],
  {
    variants: {
      layout: {
        row: 'w-full',
        tile: 'h-full min-w-[264px] flex-col',
      },
    },
    defaultVariants: { layout: 'tile' },
  }
);

/** Every media option draws in the same 40px box, so titles line up across a mixed grid. */
export const cardMediaRecipe = cva('ds-card-media flex size-10 shrink-0 items-center');

export const cardTextRecipe = cva('flex min-w-0 flex-col gap-1', {
  variants: {
    layout: {
      row: 'flex-1',
      tile: 'w-full',
    },
    /** Whether the pinned action sits beside the text rather than beside the media. */
    clearAction: { true: '', false: '' },
    /**
     * A Row whose only text is its title drops that title so its first line centres on the 40px
     * media: (40 − the 26px Heading/xs line) / 2 = 7px. Padding rather than centring the row, so
     * a title that wraps keeps its first line, the media and the action on one centre line.
     */
    titleOnMedia: {
      true: 'pt-[calc((var(--spacing)*10-var(--ds-type-heading-xs-leading))/2)]',
      false: '',
    },
  },
  compoundVariants: [
    // Figma's Row puts the action in flow, so its text column is 44px narrower (32 + the 12px gap).
    { layout: 'row', clearAction: true, className: 'pr-11' },
    // Figma has no Tile with an action and no media (its rule is "always show media"); code keeps
    // the title clear of the 32px button instead.
    { layout: 'tile', clearAction: true, className: 'pr-10' },
  ],
  defaultVariants: { layout: 'tile', clearAction: false, titleOnMedia: false },
});

export const cardStatusRecipe = cva('flex');

/** A single long word breaks inside the tile rather than pushing it wider. */
export const cardTitleRecipe = cva('ds-card-title text-heading-xs m-0 line-clamp-2 [overflow-wrap:anywhere]');

/**
 * The stretched link. Its `::after` covers the card's padding box, so a click anywhere lands on the
 * anchor; `action` and `footer` sit above it at `z-[1]`. Focus draws on the card, not the text.
 */
export const cardLinkRecipe = cva([
  'ds-card-link no-underline outline-none',
  "after:absolute after:inset-0 after:rounded-md after:content-['']",
]);

export const cardMetaRecipe = cva('ds-card-meta text-body-md m-0');
export const cardDescriptionRecipe = cva('ds-card-description text-body-md m-0');

/**
 * Pinned top-right in both layouts, so it can follow the link in the DOM and keep the tab order
 * link → action → footer. The 32px button centres on whatever the card's first line is, so the
 * "⋯", the media and the text share one centre line in every combination of parts:
 *
 *   media    the 40px media, when it heads the card: a Tile with media, or a title-only Row whose
 *            title is dropped onto the media's centre. 16 + (40 − 32) / 2 = 20.
 *   status   the 24px status Tag: 16 + (24 − 32) / 2 = 12.
 *   title    the title's first 26px line: 16 + (26 − 32) / 2 = 13, read off the type token.
 *
 * Figma pins it to the top padding instead, which sat the "⋯" 3–8px below the line it belongs to
 * (measured 2026-09-28 across 13 worst cases); this is the code's rule, and the set follows it.
 *
 * The xs IconButton draws at 32px, so the card grows its tap area to 44px from the outside
 * rather than resizing the button. That extension may belong on IconButton later (DS gap).
 */
export const cardActionRecipe = cva(
  [
    'absolute right-4 z-[1] flex',
    "[&>button]:after:absolute [&>button]:after:-inset-1.5 [&>button]:after:content-['']",
  ],
  {
    variants: {
      anchor: {
        media: 'top-5',
        status: 'top-3',
        title: 'top-[calc(var(--spacing)*4+(var(--ds-type-heading-xs-leading)-var(--spacing)*8)/2)]',
      },
    },
    defaultVariants: { anchor: 'title' },
  }
);

export const cardFooterRecipe = cva('relative z-[1] flex flex-wrap gap-1', {
  variants: {
    layout: {
      row: '',
      tile: 'mt-auto',
    },
  },
  defaultVariants: { layout: 'tile' },
});
